import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { ApiResponse } from '../types/api';
import { TenantVegetationType, ImportResult } from '../types/tenantVegetationType';

export const useTenantVegetationTypes = (enabled = true) => {
  return useQuery({
    queryKey: ['tenantVegetationTypes'],
    queryFn: () =>
      api.request<ApiResponse<TenantVegetationType[]>>('/api/tenant-vegetation-types'),
    enabled,
    refetchOnWindowFocus: false,
  });
};

interface VegetationTypePayload {
  veg_class: string;
  max_fuel_load: number;
  accumulation_rate: number;
  model_class_name: string | null;
  model_subclass?: number | null;
}

export const useCreateTenantVegetationType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VegetationTypePayload) =>
      api.post<ApiResponse<TenantVegetationType>>('/api/tenant-vegetation-types', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantVegetationTypes'] });
    },
  });
};

export const useUpdateTenantVegetationType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: Partial<VegetationTypePayload> & { id: number }) =>
      api.request<ApiResponse<TenantVegetationType>>(`/api/tenant-vegetation-types/${id}`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantVegetationTypes'] });
    },
  });
};

export const useDeleteTenantVegetationType = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) =>
      api.delete(`/api/tenant-vegetation-types/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantVegetationTypes'] });
    },
  });
};

export const useImportTenantVegetationTypes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api.post<ApiResponse<ImportResult>>('/api/tenant-vegetation-types/import-csv', {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenantVegetationTypes'] });
    },
  });
};
